import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import {
  ArrowUpRight,
  CalendarDays,
  Check,
  CheckCheck,
  Clock3,
  FilterX,
  GraduationCap,
  Inbox,
} from 'lucide-react';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import NavbarGlobal from './NavbarGlobal';
import { db } from '../firebase/firebase';
import { AppContext } from '../Context';
import { getNotificationDate, isNotificationInDateRange } from './notificationsUtils';
import './Notifications.scss';

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const formatFecha = (ts) => {
  try {
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    return Number.isNaN(date.getTime())
      ? ''
      : date.toLocaleString('es-CO', { dateStyle: 'long', timeStyle: 'short' });
  } catch {
    return '';
  }
};

const storageKey = (teacherId) => `userSeenNotifs:${teacherId}`;
const legacyStorageKey = (teacherId) => `teacherSeenNotifs:${teacherId}`;
const getSeenSet = (teacherId) => {
  try {
    const raw = localStorage.getItem(storageKey(teacherId));
    const legacyRaw = localStorage.getItem(legacyStorageKey(teacherId));
    return new Set([
      ...JSON.parse(raw || '[]'),
      ...JSON.parse(legacyRaw || '[]'),
    ]);
  } catch {
    return new Set();
  }
};
const setSeenSet = (teacherId, set) => {
  try {
    localStorage.setItem(storageKey(teacherId), JSON.stringify(Array.from(set)));
  } catch {}
};

export default function NotificationsAll() {
  const { currentUser } = useContext(AppContext);
  const teacherId = currentUser?.uid || currentUser?.id;
  const [students, setStudents] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [seen, setSeen] = useState(() => getSeenSet(teacherId));
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const unsubRefs = useRef([]);

  useEffect(() => setSeen(getSeenSet(teacherId)), [teacherId]);

  useEffect(() => {
    const load = async () => {
      const usersRef = collection(db, 'users');
      const studentsQuery = query(usersRef, where('teacherId', '==', teacherId));
      const snap = await getDocs(studentsQuery);
      const list = snap.docs
        .map(doc => ({ id: doc.id, ...(doc.data() || {}) }))
        .filter(user => !user.teacher && !user.isMaster);
      setStudents(list);
    };

    if (teacherId) load();
    return () => {
      unsubRefs.current.forEach(unsubscribe => unsubscribe?.());
      unsubRefs.current = [];
    };
  }, [teacherId]);

  useEffect(() => {
    unsubRefs.current.forEach(unsubscribe => unsubscribe?.());
    unsubRefs.current = [];
    setNotifs([]);
    if (!students.length) return undefined;

    const ids = students.map(student => student.id);
    const unsubList = chunk(ids, 10).map(ids10 => {
      const evalRef = collection(db, 'evaluaciones');
      const evaluationsQuery = query(evalRef, where('userId', 'in', ids10));
      return onSnapshot(evaluationsQuery, (snap) => {
        const arr = snap.docs.map(doc => ({ id: doc.id, ...(doc.data() || {}) }));
        setNotifs(prev => {
          const map = new Map(prev.map(notification => [notification.id, notification]));
          arr.forEach(notification => map.set(notification.id, notification));
          return Array.from(map.values());
        });
      });
    });
    unsubRefs.current = unsubList;
    return () => unsubList.forEach(unsubscribe => unsubscribe?.());
  }, [students]);

  const studentName = useMemo(() => {
    const names = new Map(students.map(student => [
      student.id,
      student.displayName || student.name || 'Alumno',
    ]));
    return (uid) => names.get(uid) || uid || 'Alumno';
  }, [students]);

  const ordered = useMemo(() => (
    [...notifs]
      .filter(notification => isNotificationInDateRange(notification, fechaDesde, fechaHasta))
      .sort((a, b) => {
        const dateA = getNotificationDate(a);
        const dateB = getNotificationDate(b);
        return (dateB?.getTime() || 0) - (dateA?.getTime() || 0);
      })
  ), [notifs, fechaDesde, fechaHasta]);

  const markSeen = (id) => {
    const next = new Set(seen);
    next.add(id);
    setSeen(next);
    setSeenSet(teacherId, next);
  };

  const markAllSeen = () => {
    const next = new Set(seen);
    ordered.forEach(notification => next.add(notification.id));
    setSeen(next);
    setSeenSet(teacherId, next);
  };

  const clearFilters = () => {
    setFechaDesde('');
    setFechaHasta('');
  };

  const unseenCount = ordered.filter(notification => !seen.has(notification.id)).length;
  const hasFilters = Boolean(fechaDesde || fechaHasta);

  return (
    <Container fluid="xl" className="notifications-page">
      <NavbarGlobal title="Notificaciones" showBackButton />

      <section className="notifications-hero">
        <div className="notifications-hero__content">
          <span className="notifications-hero__eyebrow">Seguimiento docente</span>
          <h1>Actividad de tus alumnos</h1>
          <p>
            Revisa los ejercicios recientes y accede a los intentos de cada alumno.
          </p>
        </div>

        <div className="notifications-hero__signal" aria-label={`${unseenCount} notificaciones pendientes`}>
          <span className="notifications-hero__signal-label">Sin revisar</span>
          <strong>{unseenCount}</strong>
          <span>{unseenCount === 1 ? 'actividad' : 'actividades'}</span>
          {unseenCount > 0 && (
            <Button onClick={markAllSeen}>
              <CheckCheck size={18} />
              Marcar todo como visto
            </Button>
          )}
        </div>
      </section>

      <section className="notifications-toolbar" aria-label="Filtros de notificaciones">
        <div className="notifications-toolbar__title">
          <span><CalendarDays size={20} /></span>
          <div>
            <strong>Filtrar por fecha</strong>
            <p>Acota la línea de actividad a un periodo.</p>
          </div>
        </div>
        <Form className="notifications-toolbar__form">
          <Row className="g-2">
            <Col xs={12} sm={5}>
              <Form.Label>Desde</Form.Label>
              <Form.Control
                type="date"
                value={fechaDesde}
                onChange={(event) => setFechaDesde(event.target.value)}
              />
            </Col>
            <Col xs={12} sm={5}>
              <Form.Label>Hasta</Form.Label>
              <Form.Control
                type="date"
                value={fechaHasta}
                onChange={(event) => setFechaHasta(event.target.value)}
              />
            </Col>
            <Col xs={12} sm={2} className="notifications-toolbar__clear">
              <Button
                type="button"
                onClick={clearFilters}
                disabled={!hasFilters}
                aria-label="Limpiar filtros de fecha"
              >
                <FilterX size={18} />
                <span>Limpiar</span>
              </Button>
            </Col>
          </Row>
        </Form>
      </section>

      <section className="notifications-feed">
        <header className="notifications-feed__header">
          <div>
            <span>Registro reciente</span>
            <h2>Línea de actividad</h2>
          </div>
          <span className="notifications-feed__count">
            {ordered.length} {ordered.length === 1 ? 'resultado' : 'resultados'}
          </span>
        </header>

        <div className="notifications-feed__list">
          {ordered.map((notification) => {
            const nombreAlumno = studentName(notification.userId);
            const nivel = notification?.nivel || '—';
            const modulo = notification?.modulo || '—';
            const nombre = notification?.nombre || 'Ejercicio';
            const isUnseen = !seen.has(notification.id);
            const initial = nombreAlumno.trim().charAt(0).toUpperCase() || 'A';

            return (
              <article
                key={notification.id}
                className={`notification-row${isUnseen ? ' is-unseen' : ''}`}
              >
                <div className="notification-row__rail" aria-hidden="true">
                  <span className="notification-row__avatar">{initial}</span>
                </div>

                <div className="notification-row__body">
                  <div className="notification-row__heading">
                    <div>
                      <span className="notification-row__status">
                        {isUnseen && <i />}
                        {isUnseen ? 'Nueva actividad' : 'Actividad revisada'}
                      </span>
                      <h3>{nombreAlumno}</h3>
                    </div>
                    {!isUnseen && (
                      <span className="notification-row__checked">
                        <Check size={15} />
                        Vista
                      </span>
                    )}
                  </div>

                  <p className="notification-row__description">
                    Completó <strong>{nombre}</strong>
                  </p>

                  <div className="notification-row__meta">
                    <span><GraduationCap size={15} /> Nivel {nivel}</span>
                    <span>Módulo {modulo}</span>
                    <time><Clock3 size={14} /> {formatFecha(notification?.createdAt || notification?.fecha)}</time>
                  </div>
                </div>

                <div className="notification-row__actions">
                  {isUnseen && (
                    <Button className="notification-row__mark" onClick={() => markSeen(notification.id)}>
                      <Check size={16} />
                      Marcar visto
                    </Button>
                  )}
                  <Button
                    as={Link}
                    to={`/dashboard/listado-alumnos/registro-de-practicas/${notification.userId}/ejercicio?nivel=${nivel}&modulo=${modulo}&nombre=${encodeURIComponent(nombre)}`}
                    className="notification-row__primary"
                  >
                    Ver intentos
                    <ArrowUpRight size={17} />
                  </Button>
                </div>
              </article>
            );
          })}

          {ordered.length === 0 && (
            <div className="notifications-empty">
              <span><Inbox size={30} /></span>
              <h3>{hasFilters ? 'No hay actividad en este periodo' : 'Aún no hay notificaciones'}</h3>
              <p>
                {hasFilters
                  ? 'Prueba ampliando las fechas o limpia los filtros para ver todo el historial.'
                  : 'Las actividades nuevas de tus alumnos aparecerán aquí.'}
              </p>
              {hasFilters && (
                <Button onClick={clearFilters}>
                  <FilterX size={17} />
                  Limpiar filtros
                </Button>
              )}
            </div>
          )}
        </div>
      </section>
    </Container>
  );
}
